import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "components/ui/button";
import useFlowAssignments from "hooks/useFlowAssignments";
import { CourseCover } from "pages/MyTraining/coursePresentation";
import { FlowAssignment } from "types";
import { getFlowAssignmentImageUrl } from "utils/courseImagePreload";
import { buildFlowPath } from "utils/flowNavigation";

const MAX_VISIBLE_QUICK_WINS = 4;

const sortAssignmentsByOrder = (assignments: FlowAssignment[]): FlowAssignment[] => {
  return assignments
    .map((assignment, index) => ({ assignment, index }))
    .sort((left, right) => {
      const leftInProgress = Boolean(left.assignment.has_started) && !Boolean(left.assignment.is_completed);
      const rightInProgress = Boolean(right.assignment.has_started) && !Boolean(right.assignment.is_completed);

      if (leftInProgress !== rightInProgress) {
        return leftInProgress ? -1 : 1;
      }

      const leftCompleted = Boolean(left.assignment.is_completed);
      const rightCompleted = Boolean(right.assignment.is_completed);

      if (leftCompleted !== rightCompleted) {
        return leftCompleted ? 1 : -1;
      }

      const leftOrder =
        typeof left.assignment.order === "number"
          ? left.assignment.order
          : Number.MAX_SAFE_INTEGER;
      const rightOrder =
        typeof right.assignment.order === "number"
          ? right.assignment.order
          : Number.MAX_SAFE_INTEGER;

      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      const leftAssignedAt = Date.parse(left.assignment.assigned_at ?? "");
      const rightAssignedAt = Date.parse(right.assignment.assigned_at ?? "");
      const leftHasAssignedAt = Number.isFinite(leftAssignedAt);
      const rightHasAssignedAt = Number.isFinite(rightAssignedAt);

      if (leftHasAssignedAt && rightHasAssignedAt && leftAssignedAt !== rightAssignedAt) {
        return rightAssignedAt - leftAssignedAt;
      }

      return left.index - right.index;
    })
    .map(({ assignment }) => assignment);
};

export const QuickWinsSection = () => {
  const navigate = useNavigate();
  const { data: assignments, loading, error } = useFlowAssignments();
  const visibleAssignments = useMemo(() => sortAssignmentsByOrder(assignments).slice(0, MAX_VISIBLE_QUICK_WINS), [assignments]);

  return (
    <section aria-labelledby="home-quick-wins-heading">
      <div className="home-section-heading">
        <div><h2 id="home-quick-wins-heading">Quick Wins</h2><p className="home-quick-wins__description">Short lessons you can finish in one sitting.</p></div>
        <Link to="/training#quick-wins">See all <span aria-hidden="true">→</span></Link>
      </div>
      {loading ? <p className="home-state" role="status">Loading Quick Wins…</p> : null}
      {!loading && error ? <div className="home-state"><p>Unable to load your quick wins right now.</p><Button variant="secondary" onClick={() => navigate("/training#quick-wins")}>Open Quick Wins</Button></div> : null}
      {!loading && !error && !visibleAssignments.length ? <div className="home-state"><p>No quick wins are assigned yet.</p><Button variant="secondary" onClick={() => navigate("/training#quick-wins")}>Browse Quick Wins</Button></div> : null}
      {!loading && !error ? <div className="home-quick-wins__list">
        {visibleAssignments.map((assignment) => {
          const flow = assignment.flow;
          const title = flow.title || flow.name || "Untitled module";
          const progress = assignment.progress_percent;
          const showProgress = assignment.has_started && !assignment.is_completed && !flow.is_quiz && typeof progress === "number" && Number.isFinite(progress);
          const progressPercent = showProgress ? Math.min(100, Math.max(0, Math.round(progress))) : 0;
          return <Link key={assignment.id} className="home-quick-win" to={buildFlowPath({ flowName: flow.name })}>
            <CourseCover courseId={flow.id} name={title} source={getFlowAssignmentImageUrl(assignment)} fallbackArtwork="mountains" />
            <span className="home-quick-win__copy"><strong>{title}</strong>
              <span className="home-quick-win__progress">{assignment.is_completed ? "Completed" : assignment.has_started ? (showProgress ? <>{progressPercent}% done<span className="sr-only"> · In progress</span></> : "In progress") : "Not started"}</span>
            </span>
          </Link>;
        })}
      </div> : null}
    </section>
  );
};
